import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Shield,
  Loader2,
  Pencil,
  Plug,
  Store,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BUILTIN_PROVIDER_INFO = {
  openai: { name: "OpenAI", accent: "#10A37F", icon: "O", placeholder: "sk-..." },
  anthropic: { name: "Anthropic", accent: "#F97316", icon: "A", placeholder: "sk-ant-..." },
  google: { name: "Google (Gemini)", accent: "#1D4ED8", icon: "G", placeholder: "AIza..." },
  groq: { name: "Groq", accent: "#4338CA", icon: "Q", placeholder: "gsk_..." },
};

// Build dynamic provider info from backend data
function getProviderInfo(p) {
  const builtin = BUILTIN_PROVIDER_INFO[p.slug];
  if (builtin) return builtin;
  // Dynamic: marketplace or custom providers
  return {
    name: p.name,
    accent: p.accent || "#6B7280",
    icon: p.name?.[0]?.toUpperCase() || "?",
    placeholder: "Enter your API key...",
  };
}

export default function ApiKeysPage({ providers, onRefresh, api }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditProvider, setCreditProvider] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [savingCredit, setSavingCredit] = useState(false);
  const [fetchingBalance, setFetchingBalance] = useState({});
  const [fetchResult, setFetchResult] = useState(null);
  const [fetchResultDialogOpen, setFetchResultDialogOpen] = useState(false);

  const handleSaveKey = async () => {
    if (!selectedProvider || !apiKeyInput.trim()) {
      toast.error("Please select a provider and enter an API key");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${api}/keys`, {
        provider: selectedProvider,
        api_key: apiKeyInput.trim(),
      });
      const isValid = res.data?.is_valid;
      const pInfo = providers.find(p => p.slug === selectedProvider);
      const pName = pInfo ? getProviderInfo(pInfo).name : selectedProvider;
      if (isValid === true) {
        toast.success(`API key saved and validated for ${pName}`);
      } else if (isValid === false) {
        toast.error(`Key saved but validation failed for ${pName}. Please check your key.`);
      } else {
        toast.success(`API key saved for ${pName}`);
      }
      setDialogOpen(false);
      setApiKeyInput("");
      setSelectedProvider("");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save key");
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (provider) => {
    setValidating((prev) => ({ ...prev, [provider]: true }));
    try {
      const res = await axios.post(`${api}/keys/${provider}/validate`);
      const pInfo = providers.find(p => p.slug === provider);
      const pName = pInfo ? getProviderInfo(pInfo).name : provider;
      if (res.data.valid) {
        toast.success(`${pName} key is valid!`);
      } else {
        toast.error(`${pName}: ${res.data.message}`);
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Validation failed");
    } finally {
      setValidating((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${api}/keys/${deleting}`);
      toast.success(`Key for ${providers.find(p => p.slug === deleting)?.name || deleting} deleted`);
      setDeleteDialogOpen(false);
      setDeleting(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete key");
    }
  };

  const handleSaveCredit = async () => {
    if (!creditProvider || !creditAmount) {
      toast.error("Please enter a credit amount");
      return;
    }
    setSavingCredit(true);
    try {
      await axios.put(`${api}/credits/${creditProvider}`, {
        balance: parseFloat(creditAmount),
        currency: "USD",
      });
      toast.success(`Credit balance updated for ${providers.find(p => p.slug === creditProvider)?.name || creditProvider}`);
      setCreditDialogOpen(false);
      setCreditAmount("");
      setCreditProvider("");
      onRefresh();
    } catch (err) {
      toast.error("Failed to update credit balance");
    } finally {
      setSavingCredit(false);
    }
  };

  const handleFetchBalance = async (provider) => {
    setFetchingBalance((prev) => ({ ...prev, [provider]: true }));
    try {
      const res = await axios.post(`${api}/credits/${provider}/fetch`);
      const data = res.data;
      if (data.available && data.balance !== null && data.balance !== undefined) {
        toast.success(`Balance fetched for ${providers.find(p => p.slug === provider)?.name || provider}: $${data.balance}`);
        onRefresh();
      } else if (data.available && data.spend_data) {
        setFetchResult({ provider, ...data });
        setFetchResultDialogOpen(true);
        onRefresh();
      } else {
        setFetchResult({ provider, ...data });
        setFetchResultDialogOpen(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch balance");
    } finally {
      setFetchingBalance((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const getStatusBadge = (keyStatus) => {
    if (!keyStatus) return null;
    if (keyStatus.is_valid === true) {
      return (
        <Badge variant="secondary" className="gap-1 text-green-700 bg-green-50" data-testid="api-keys-status-badge">
          <span className="status-dot status-dot-success" /> Valid
        </Badge>
      );
    }
    if (keyStatus.is_valid === false) {
      return (
        <Badge variant="secondary" className="gap-1 text-red-700 bg-red-50" data-testid="api-keys-status-badge">
          <span className="status-dot status-dot-error" /> Invalid
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1" data-testid="api-keys-status-badge">
        <span className="status-dot status-dot-warning" /> Not validated
      </Badge>
    );
  };

  return (
    <div className="main-content-inner">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your LLM provider API keys</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreditDialogOpen(true)}
            className="gap-2"
          >
            <span className="hidden sm:inline">Set</span> Credit
          </Button>
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            data-testid="api-keys-add-key-button"
            className="gap-2"
          >
            <Plus className="w-4 h-4" /> Add Key
          </Button>
        </div>
      </div>

      {/* Info */}
      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium">Your keys are encrypted at rest</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              API keys are encrypted using AES-256 before storage. They are only decrypted when making LLM requests.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Provider</TableHead>
              <TableHead>Key (Masked)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Credit Balance</TableHead>
              <TableHead>Last Validated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {providers.map((p) => {
              const info = getProviderInfo(p);
              const hasKey = !!p.key_status;
              const isCustom = p.source === "marketplace" || p.source === "custom";
              return (
                <TableRow key={p.slug}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: info?.accent }}
                      >
                        {isCustom ? <Plug className="w-3.5 h-3.5" /> : info?.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium">{p.name}</span>
                          {isCustom && (
                            <Badge variant="outline" className="text-[8px] h-3.5 px-1 gap-0.5">
                              <Store className="w-2 h-2" /> {p.source}
                            </Badge>
                          )}
                        </div>
                        {p.billing_url ? (
                          <a
                            href={p.billing_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[11px] text-primary hover:underline"
                          >
                            Billing <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-[11px] text-muted-foreground font-mono">{p.slug}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {hasKey ? (
                      <code className="font-mono text-xs bg-muted px-2 py-1 rounded" data-testid="api-keys-masked-key-value">
                        {(() => {
                          const mk = p.key_status.masked_key || "";
                          if (mk.length <= 14) return mk;
                          return mk.slice(0, 5) + "····" + mk.slice(-4);
                        })()}
                      </code>
                    ) : (
                      <span className="text-xs text-muted-foreground">No key stored</span>
                    )}
                  </TableCell>
                  <TableCell>{hasKey ? getStatusBadge(p.key_status) : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                  <TableCell>
                    {p.credit ? (
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-sm tabular-nums font-medium">${p.credit.balance.toFixed(2)}</span>
                        <button
                          className="text-[10px] text-primary hover:underline ml-1"
                          onClick={(e) => { e.stopPropagation(); setCreditProvider(p.slug); setCreditAmount(p.credit.balance.toString()); setCreditDialogOpen(true); }}
                        >edit</button>
                        {hasKey && (
                          <button
                            className="text-[10px] text-muted-foreground hover:text-primary hover:underline ml-1"
                            disabled={fetchingBalance[p.slug]}
                            onClick={(e) => { e.stopPropagation(); handleFetchBalance(p.slug); }}
                          >
                            {fetchingBalance[p.slug] ? "fetching..." : "refresh"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {hasKey && (
                          <button
                            className="text-xs text-primary hover:underline font-medium"
                            disabled={fetchingBalance[p.slug]}
                            onClick={(e) => { e.stopPropagation(); handleFetchBalance(p.slug); }}
                            data-testid={`fetch-balance-${p.slug}`}
                          >
                            {fetchingBalance[p.slug] ? (
                              <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Fetching...</span>
                            ) : (
                              "Fetch Balance"
                            )}
                          </button>
                        )}
                        <button
                          className="text-xs text-muted-foreground hover:text-primary hover:underline"
                          onClick={(e) => { e.stopPropagation(); setCreditProvider(p.slug); setCreditAmount(""); setCreditDialogOpen(true); }}
                        >
                          + Manual
                        </button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.key_status?.last_validated ? (
                      <span className="text-xs text-muted-foreground">
                        {new Date(p.key_status.last_validated).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {hasKey && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedProvider(p.slug);
                              setApiKeyInput("");
                              setDialogOpen(true);
                            }}
                            data-testid="api-keys-edit-key-button"
                            className="h-8 text-xs gap-1"
                          >
                            <Pencil className="w-3 h-3" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleValidate(p.slug)}
                            disabled={validating[p.slug]}
                            data-testid="api-keys-validate-key-button"
                            className="h-8 text-xs gap-1"
                          >
                            {validating[p.slug] ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3" />
                            )}
                            Validate
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeleting(p.slug);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid="api-keys-delete-key-button"
                            className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </Button>
                        </>
                      )}
                      {!hasKey && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedProvider(p.slug);
                            setDialogOpen(true);
                          }}
                          className="h-8 text-xs gap-1"
                        >
                          <Plus className="w-3 h-3" /> Add Key
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Add/Edit Key Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{providers.find(p => p.slug === selectedProvider)?.key_status ? "Update" : "Add"} API Key</DialogTitle>
            <DialogDescription>
              {providers.find(p => p.slug === selectedProvider)?.key_status
                ? `Enter a new API key to replace the existing one for ${providers.find(p => p.slug === selectedProvider)?.name}. It will be encrypted and auto-validated.`
                : "Enter your API key for the selected provider. It will be encrypted and auto-validated."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="provider-select">Provider</Label>
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger id="provider-select">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getProviderInfo(p).accent }} />
                        {p.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-key-input">API Key</Label>
              <Input
                id="api-key-input"
                type="password"
                placeholder={selectedProvider ? getProviderInfo(providers.find(p => p.slug === selectedProvider) || {slug: selectedProvider})?.placeholder : "Enter your API key"}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveKey} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {saving ? "Saving & Validating..." : "Save & Validate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the API key for {providers.find(p => p.slug === deleting)?.name || deleting}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credit Balance Dialog */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Credit Balance</DialogTitle>
            <DialogDescription>Manually enter your current credit balance for a provider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="credit-provider-select">Provider</Label>
              <Select value={creditProvider} onValueChange={setCreditProvider}>
                <SelectTrigger id="credit-provider-select">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="credit-amount">Balance (USD)</Label>
              <Input
                id="credit-amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g. 25.00"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCredit} disabled={savingCredit}>
              {savingCredit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fetch Balance Result Dialog */}
      <Dialog open={fetchResultDialogOpen} onOpenChange={setFetchResultDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Balance Fetch Result</DialogTitle>
            <DialogDescription>
              {fetchResult?.provider && (providers.find(p => p.slug === fetchResult.provider)?.name || fetchResult.provider)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {fetchResult?.available ? (
              <>
                {fetchResult.spend_data && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Billing Data Retrieved:</p>
                    <div className="bg-muted rounded-md p-3 space-y-1">
                      {Object.entries(fetchResult.spend_data).map(([key, val]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                          <span className="font-mono font-medium">
                            {typeof val === 'number' ? `$${val.toFixed(4)}` : String(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {fetchResult.details && (
                  <p className="text-sm text-muted-foreground">{fetchResult.details}</p>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-200 dark:border-amber-800">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Auto-fetch not available</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">{fetchResult?.details}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  You can manually set your balance by clicking the "+ Manual" button next to the provider.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            {!fetchResult?.available && fetchResult?.provider && (
              <Button
                variant="outline"
                onClick={() => {
                  setFetchResultDialogOpen(false);
                  setCreditProvider(fetchResult.provider);
                  setCreditAmount("");
                  setCreditDialogOpen(true);
                }}
              >
                Set Manually
              </Button>
            )}
            <Button onClick={() => setFetchResultDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
